// Grid hexagonal do tabuleiro (T-SHARED-02).
// Hex "pointy-top", layout offset "odd-r" (linhas ímpares deslocadas meia
// célula para a direita), 7 colunas x 8 linhas. Linhas 0-3 = lado de cima
// (oponente), 4-7 = lado de baixo (jogador). Cada lado vê o próprio tabuleiro
// nas linhas 4-7; o espelhamento é feito por `mirror`.
// Só aritmética inteira: determinístico em qualquer engine.

export const BOARD_COLS = 7;
export const BOARD_ROWS = 8;
export const SIDE_ROWS = 4;
export const CELL_COUNT = BOARD_COLS * BOARD_ROWS;

export interface Offset { col: number; row: number }
export interface Axial { q: number; r: number }

/** índice linear 0..55 */
export type CellIndex = number;

export function inBounds(col: number, row: number): boolean {
  return col >= 0 && col < BOARD_COLS && row >= 0 && row < BOARD_ROWS;
}

export function toIndex(col: number, row: number): CellIndex {
  return row * BOARD_COLS + col;
}

export function fromIndex(i: CellIndex): Offset {
  return { col: i % BOARD_COLS, row: Math.floor(i / BOARD_COLS) };
}

export function offsetToAxial(col: number, row: number): Axial {
  return { q: col - (row - (row & 1)) / 2, r: row };
}

export function axialToOffset(q: number, r: number): Offset {
  return { col: q + (r - (r & 1)) / 2, row: r };
}

/** Distância em hexes entre duas células (índices). */
export function distance(a: CellIndex, b: CellIndex): number {
  const ar = Math.floor(a / BOARD_COLS), ac = a % BOARD_COLS;
  const br = Math.floor(b / BOARD_COLS), bc = b % BOARD_COLS;
  const aq = ac - (ar - (ar & 1)) / 2;
  const bq = bc - (br - (br & 1)) / 2;
  const dq = aq - bq, dr = ar - br;
  return (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2;
}

// Direções em axial (q, r)
const DIRS: readonly [number, number][] = [
  [1, 0], [1, -1], [0, -1], [-1, 0], [-1, 1], [0, 1],
];

/** Vizinhos pré-calculados por célula (ordem estável). */
export const NEIGHBORS: readonly (readonly CellIndex[])[] = (() => {
  const out: CellIndex[][] = [];
  for (let i = 0; i < CELL_COUNT; i++) {
    const { col, row } = fromIndex(i);
    const { q, r } = offsetToAxial(col, row);
    const list: CellIndex[] = [];
    for (const [dq, dr] of DIRS) {
      const o = axialToOffset(q + dq, r + dr);
      if (inBounds(o.col, o.row)) list.push(toIndex(o.col, o.row));
    }
    out.push(list);
  }
  return out;
})();

/** Todas as células a até `radius` hexes de `center` (inclui o centro), em ordem de índice. */
export function cellsInRadius(center: CellIndex, radius: number): CellIndex[] {
  const out: CellIndex[] = [];
  for (let i = 0; i < CELL_COUNT; i++) if (distance(center, i) <= radius) out.push(i);
  return out;
}

/**
 * Linha de células de `a` até `b` (inclusive), por interpolação em coordenadas
 * cúbicas com arredondamento. Usada por habilidades em linha.
 */
export function line(a: CellIndex, b: CellIndex): CellIndex[] {
  const n = distance(a, b);
  const pa = fromIndex(a), pb = fromIndex(b);
  const A = offsetToAxial(pa.col, pa.row), B = offsetToAxial(pb.col, pb.row);
  const out: CellIndex[] = [];
  for (let i = 0; i <= n; i++) {
    // pequeno epsilon evita empates de arredondamento sempre no mesmo sentido
    const t = n === 0 ? 0 : i / n;
    const q = A.q + (B.q - A.q) * t + 1e-6;
    const r = A.r + (B.r - A.r) * t + 1e-6;
    const { q: rq, r: rr } = cubeRound(q, r);
    const o = axialToOffset(rq, rr);
    if (inBounds(o.col, o.row)) out.push(toIndex(o.col, o.row));
  }
  return out;
}

function cubeRound(q: number, r: number): Axial {
  const s = -q - r;
  let rq = Math.round(q), rr = Math.round(r);
  const rs = Math.round(s);
  const dq = Math.abs(rq - q), dr = Math.abs(rr - r), ds = Math.abs(rs - s);
  if (dq > dr && dq > ds) rq = -rr - rs;
  else if (dr > ds) rr = -rq - rs;
  return { q: rq, r: rr };
}

/** Espelha uma célula para o outro lado (o tabuleiro do oponente é girado 180°). */
export function mirror(i: CellIndex): CellIndex {
  return CELL_COUNT - 1 - i;
}

/** Linha pertence ao lado do jogador local (4..7)? */
export function isPlayerSide(i: CellIndex): boolean {
  return Math.floor(i / BOARD_COLS) >= SIDE_ROWS;
}

/**
 * A* em grid hexagonal. `blocked[i]` = célula ocupada (ignorada se for o destino).
 * Retorna o caminho sem a origem (primeiro passo em [0]) ou [] se não houver.
 * Desempate estável: menor f, depois menor h, depois menor índice.
 * Buffers reutilizáveis evitam alocação por chamada no loop de combate.
 */
export class Pathfinder {
  private g = new Int32Array(CELL_COUNT);
  private f = new Int32Array(CELL_COUNT);
  private from = new Int32Array(CELL_COUNT);
  private state = new Uint8Array(CELL_COUNT); // 0 = novo, 1 = aberto, 2 = fechado
  private open = new Int32Array(CELL_COUNT);

  find(start: CellIndex, goal: CellIndex, blocked: ArrayLike<boolean | number>): CellIndex[] {
    if (start === goal) return [];
    this.state.fill(0);
    let openLen = 0;
    this.g[start] = 0;
    this.f[start] = distance(start, goal);
    this.from[start] = -1;
    this.state[start] = 1;
    this.open[openLen++] = start;

    while (openLen > 0) {
      // menor f (lista pequena: 56 células, varredura linear é mais rápida que heap)
      let bi = 0;
      for (let k = 1; k < openLen; k++) {
        const a = this.open[k]!, b = this.open[bi]!;
        if (
          this.f[a]! < this.f[b]! ||
          (this.f[a] === this.f[b] && (distance(a, goal) < distance(b, goal) || (distance(a, goal) === distance(b, goal) && a < b)))
        ) bi = k;
      }
      const cur = this.open[bi]!;
      this.open[bi] = this.open[--openLen]!;
      if (cur === goal) return this.rebuild(goal);
      this.state[cur] = 2;

      for (const nb of NEIGHBORS[cur]!) {
        if (this.state[nb] === 2) continue;
        if (blocked[nb] && nb !== goal) continue;
        const g = this.g[cur]! + 1;
        if (this.state[nb] === 1 && g >= this.g[nb]!) continue;
        this.g[nb] = g;
        this.f[nb] = g + distance(nb, goal);
        this.from[nb] = cur;
        if (this.state[nb] !== 1) {
          this.state[nb] = 1;
          this.open[openLen++] = nb;
        }
      }
    }
    return [];
  }

  private rebuild(goal: CellIndex): CellIndex[] {
    const path: CellIndex[] = [];
    for (let c = goal; this.from[c] !== -1; c = this.from[c]!) path.push(c);
    return path.reverse();
  }
}

/**
 * Célula livre mais próxima de `target` dentro de `range` hexes, alcançável a
 * partir de `start`. Útil para "andar até ficar no alcance".
 */
export function nearestFreeInRange(
  start: CellIndex,
  target: CellIndex,
  range: number,
  blocked: ArrayLike<boolean | number>,
): CellIndex | -1 {
  let best = -1, bestD = Infinity;
  for (let i = 0; i < CELL_COUNT; i++) {
    if (i !== start && blocked[i]) continue;
    if (distance(i, target) > range) continue;
    const d = distance(start, i);
    if (d < bestD) { bestD = d; best = i; }
  }
  return best;
}
